/**
 * CORE-883 (QA 23.10) — лента дописывается страницами, а сервер под ней живёт.
 *
 * Ручка ленты листает смещением (`offset` + `limit`), курсора у неё нет.
 * Смещение считает строки от верха, а верх меняется, пока человек читает
 * первую страницу:
 *
 * - новое уведомление сдвигает ленту вниз на строку, и «Показать ещё»
 *   приносит последнюю строку экрана второй раз, а самого нового не видно
 *   (QA: 35 строк, пришла 36-я — «строка 30» дважды);
 * - отозванное (перенос и отмена эфира снимают напоминание, CORE-992)
 *   сдвигает ленту вверх, и строка на стыке страниц не приходит вовсе — ни
 *   на этой странице, ни на следующей.
 *
 * Поэтому экран держит одно правило: его список — непрерывное начало
 * серверной ленты. Низ дописывается от последней показанной строки, а не от
 * их числа: страница берётся с одной строкой внахлёст, и по ней видно,
 * сдвинулась ли лента. Верх сверяется с первой страницей сервера — так
 * появляются новые уведомления и уходят отозванные.
 *
 * Курсор на сервере (`before={id}`) снял бы всё это одним запросом. Пока его
 * нет, сдвиг ловится здесь. Модуль без React Native — проверяется под jest.
 */
import type { AppNotification, NotificationFeed } from '../../types/notifications';

/**
 * Порядок ленты на сервере: новые сверху, при равном времени — больший id
 * (`OrderByDescending(CreatedAtUtc).ThenByDescending(Id)`). Отрицательное —
 * `a` стоит выше `b`.
 */
export function compareFeedOrder(a: AppNotification, b: AppNotification): number {
  const ta = Date.parse(a.createdAtUtc);
  const tb = Date.parse(b.createdAtUtc);
  if (!Number.isNaN(ta) && !Number.isNaN(tb) && ta !== tb) return tb - ta;
  return b.id - a.id;
}

/** Одна страница ленты: `offset` строк пропустить, взять `limit`. */
export type FeedPageFetcher = (offset: number, limit: number) => Promise<NotificationFeed>;

export interface FeedPage {
  /** Строки для конца списка, в порядке сервера. Тех, что уже на экране, среди них нет. */
  items: AppNotification[];
  /** Есть ли на сервере что-то дальше принесённого. */
  hasMore: boolean;
}

// Сколько запросов одно «Показать ещё» может сделать, прежде чем перестать
// сверять стык. Сдвиг окна — шаг в страницу, так что это несколько страниц
// отозванных строк выше последней показанной: больше за время чтения одной
// страницы не снимает ничто.
const MAX_REQUESTS = 8;

function unseen(rows: AppNotification[], seen: Set<number>): AppNotification[] {
  const out: AppNotification[] = [];
  for (const row of rows) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    out.push(row);
  }
  return out;
}

/**
 * Следующая страница после того, что уже показано.
 *
 * Окно запрашивается с последней показанной строки (`offset = n − 1`):
 *
 * - она на месте — всё, что ниже неё, и есть продолжение (новые сверху
 *   сдвинули её вниз — значит, в окне она не первая, и строки над ней уже на
 *   экране);
 * - окно начинается ниже неё — выше что-то отозвали, лента поднялась, и
 *   строки между ней и окном пропали бы: окно сдвигается назад;
 * - окно целиком выше неё — сверху пришло больше, чем окно: вперёд;
 * - она внутри окна по порядку, но её самой нет — её отозвали: продолжение —
 *   всё, что старше неё.
 *
 * Сдвинутое назад окно приносит меньше страницы — тогда дочитываем от его
 * конца: там позиция уже известна, а не угадана.
 */
export async function fetchFeedPageAfter(
  current: AppNotification[],
  fetchPage: FeedPageFetcher,
  pageSize: number,
): Promise<FeedPage> {
  const seen = new Set(current.map((n) => n.id));
  let anchor = current[current.length - 1];
  if (!anchor) {
    const res = await fetchPage(0, pageSize);
    const items = res.items ?? [];
    return { items: unseen(items, seen), hasMore: items.length < (res.totalCount ?? 0) };
  }

  const limit = pageSize + 1;
  let offset = current.length - 1;
  const out: AppNotification[] = [];
  let hasMore = false;
  for (let request = 1; request <= MAX_REQUESTS; request++) {
    const res = await fetchPage(offset, limit);
    const window = res.items ?? [];
    hasMore = offset + window.length < (res.totalCount ?? 0);
    const lastTry = request === MAX_REQUESTS;

    const at = window.findIndex((n) => n.id === anchor.id);
    if (at < 0 && !lastTry) {
      const first = window[0];
      if (offset > 0 && (first == null || compareFeedOrder(first, anchor) > 0)) {
        offset = Math.max(0, offset - pageSize);
        continue;
      }
      const tail = window[window.length - 1];
      if (tail != null && compareFeedOrder(tail, anchor) < 0 && hasMore) {
        offset += window.length;
        continue;
      }
    }
    // На последней попытке стык не сверен, но дублей не будет: берём только
    // то, чего на экране нет и что старше последней строки.
    const after = at >= 0 ? window.slice(at + 1) : window.filter((n) => compareFeedOrder(n, anchor) > 0);
    out.push(...unseen(after, seen));
    if (out.length >= pageSize || !hasMore || lastTry || window.length === 0) break;
    anchor = window[window.length - 1];
    offset += window.length - 1;
  }
  // Страница — это страница: лишнее из дочитанного окна придёт следующим нажатием.
  if (out.length > pageSize) return { items: out.slice(0, pageSize), hasMore: true };
  return { items: out, hasMore };
}

export interface FeedHead {
  items: AppNotification[];
  hasMore: boolean;
}

/**
 * Сверить верх списка с первой страницей сервера (`offset = 0`).
 *
 * Первая страница заменяет собой всё, что в её пределах: новые встают сверху,
 * отозванные уходят, прочитанность берётся свежая. Ниже её последней строки
 * список остаётся как был — сервер этим ответом о нём ничего не сказал.
 *
 * Прочитанное не становится непрочитанным: ответ мог уйти раньше, чем
 * отметка, сделанная на экране, дошла до сервера, а обратной отметки в
 * интерфейсе нет.
 */
export function mergeFeedHead(current: AppNotification[], head: NotificationFeed, hasMore: boolean): FeedHead {
  const local = new Map(current.map((n) => [n.id, n]));
  const top = (head.items ?? []).map((n) => (local.get(n.id)?.isRead && !n.isRead ? { ...n, isRead: true } : n));
  const total = head.totalCount ?? top.length;
  // Первая страница — вся лента: сверять не с чем, она и есть ответ.
  if (top.length >= total) return { items: top, hasMore: false };

  const boundary = top[top.length - 1];
  // Пустая первая страница при непустой ленте — ответ, которому нечего
  // сказать о списке. Оставляем как было.
  if (!boundary) return { items: current, hasMore };
  const inTop = new Set(top.map((n) => n.id));
  // Новых пришло больше страницы: первая страница кончается выше первой
  // строки экрана, и что между ними — неизвестно. Склеить их значило бы
  // показать ленту с дырой; начинаем список заново с верха.
  const firstShown = current[0];
  if (firstShown && compareFeedOrder(boundary, firstShown) < 0) return { items: top, hasMore: true };
  const below = current.filter((n) => !inTop.has(n.id) && compareFeedOrder(n, boundary) > 0);
  // Ниже первой страницы ничего не было — значит, дальше неё есть что листать.
  return { items: [...top, ...below], hasMore: below.length > 0 ? hasMore : true };
}

/** Дописать страницу в конец, не повторив того, что уже есть. */
export function appendFeedPage(current: AppNotification[], page: AppNotification[]): AppNotification[] {
  const seen = new Set(current.map((n) => n.id));
  const fresh = unseen(page, seen);
  return fresh.length ? [...current, ...fresh] : current;
}
